<template>
  <TestRenderer
  :testData="testData"
  :questionIndexMap="questionIndexMap"
  :readOnly="publicationStore.publicationData?.readOnlyAnswer || false"
  :visitedPages="visitedPages"
  :lockedQuestions="lockedQuestions"
  :showTestMap="showTestMap"
  :currentPageIndex="currentPageIndex"
  :lastPageReached="lastPageReached"
  :answers="answers"
  :stopTimer="stopTimer"
  :onPrevPage="prevPage"
  :onNextPage="wrappedNextPage"
  :onSendResults="afterTest"
  :handleNavigateToPage="wrappedGoToPage"
  @time-up="onTimeUp"
  @toggle-test-map="toggleTestMap"
  @lock-question="handleLockQuestion"
/>

</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { useResponsesStore } from '@/stores/useResponsesStore';
import { usePublicationStore } from '@/stores/usePublicationStore';
import TestRenderer from './TestRenderer.vue';
import { useTestNavigation } from '@/testRenderUtil/useTestNavigation';
import { useTestDataLoader } from '@/testRenderUtil/useTestDataLoader';
import { useSessionManager } from '@/testRenderUtil/useSessionManager';
import { useResultSender } from '@/testRenderUtil/useResultSender';

const lockedQuestions = ref(new Set());
const route = useRoute();
const router = useRouter();
const publicationStore = usePublicationStore();
const responsesStore = useResponsesStore();

const testId = ref(route.query.testId);
const publicationId = route.query.publicationId;
const groupId = route.query.groupId;
const adaptiveMode = ref(route.query.mode === 'adaptive');
const stopTimer = ref(false);

// Log des paramètres pour diagnostic
console.log('🎯 TestRunner initialized with:', {
  testId: testId.value,
  publicationId,
  groupId,
  adaptiveMode: adaptiveMode.value,
  allParams: route.query
});
const showTestMap = ref(false);
const lastPageReached = ref(false);
const toggleTestMap = () => showTestMap.value = !showTestMap.value;
const lastPageChanger = () => lastPageReached.value = !lastPageReached.value;
const questionIndexMap = ref({});
const visitedPages = ref(new Set()); // 🆕 Ajout ici !

const { testData, fetchTestData } = useTestDataLoader(testId);
const { currentPageIndex, prevPage, nextPage, goToPage, getQuestionIndexMap } = useTestNavigation(testData);

const token = localStorage.getItem('token');
let userId = null;
if (token) {
  try {
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    userId = decodedToken?._id || decodedToken?.sub;
  } catch (e) {
    console.error("Token decoding failed:", e);
  }
}
 // J'ai besoin de récupérer la valeur access de la publication



let sessionManager = null;

//const publicationOwnerId = publicationStore.publicationData?.owner;

const { sendResultsToDatabase } = useResultSender(responsesStore);


const answers = computed(() => {
  const result = responsesStore.responses.reduce((acc, r) => {
    acc[r.questionId] = r.answered;
    return acc;
  }, {});
  
  // Debug pour textGaps et Match spécifiquement
  const specialAnswers = Object.keys(result).filter(key => 
    key.startsWith('textGaps') || key.includes('Gap') ||
    key.startsWith('match') || key.includes('Match')
  );
  
  if (specialAnswers.length > 0) {
    console.log('🎯 TestRunner - Special answers assembled:', {
      specialAnswers,
      allAnswers: result,
      responsesStore: responsesStore.responses.filter(r => 
        r.questionId.startsWith('textGaps') || r.questionId.includes('Gap') ||
        r.questionId.startsWith('match') || r.questionId.includes('Match')
      )
    });
  }
  
  return result;
});

function handleLockQuestion(questionId) {
  lockedQuestions.value.add(questionId);
}

function lockCurrentPageQuestions() {
  const currentPage = testData.value.pages[currentPageIndex.value];
  if (!currentPage) return;

  for (const child of currentPage.children) {
    const answer = answers.value[child.id];

    const isAnswered =
      answer !== undefined &&
      answer !== null &&
      (!Array.isArray(answer) || answer.length > 0) &&
      (typeof answer !== 'string' || answer.trim() !== '');

    if (isAnswered) {
      lockedQuestions.value.add(child.id);
    }
  }
}



// 🚀 Wrappers pour enregistrer la page visitée au bon moment
function wrappedNextPage() {
  lockCurrentPageQuestions(); // 🆕 Avant de changer de page
  visitedPages.value.add(currentPageIndex.value);
  nextPage();
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function wrappedGoToPage(index) {
  if (index > currentPageIndex.value) {
    lockCurrentPageQuestions(); // 🆕 Avant de sauter à une page suivante
    visitedPages.value.add(currentPageIndex.value);
  }
  goToPage(index);
  window.scrollTo({ top: 0, behavior: 'smooth' });
}


async function afterTest() {
  // ✅ PROTECTION: Refuser de sauvegarder les tests CAT en mode linéaire
  if (testData.value && testData.value.progression === 'adaptive') {
    console.error('❌ CRITICAL: CAT test attempted to save as LINEAR in TestRunner!');
    console.error('🔄 Redirecting to AdaptiveTestRunner...');
    const currentQuery = new URLSearchParams(route.query).toString();
    router.replace(`/adaptive-test-runner?${currentQuery}`);
    return;
  }
  
  stopTimer.value = true;
  lastPageChanger();
  
  // 🤖 [AGENT SYNC] Attendre la completion des ShortAnswer avant envoi (mode agent uniquement)
  const isAgentMode = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.agent === true;
    } catch {
      return false;
    }
  };
  
  // SEULEMENT pour les agents : attendre que les ShortAnswer soient complétés
  if (isAgentMode()) {
    console.log('🤖 [TESTRUNNER] Agent mode detected - waiting for ShortAnswer completions before sending results...');
    
    const waitForAllShortAnswersCompletion = async (maxWaitTime = 30000) => {
      return new Promise((resolve) => {
        console.log('🤖 [TESTRUNNER] Checking for pending ShortAnswer completions...');
        
        const startTime = Date.now();
        const checkInterval = 500; // Vérifier toutes les 500ms
        
        const checkCompletion = () => {
          const pendingShortAnswers = document.querySelectorAll('textarea[data-shortanswer-generating="true"]');
          
          console.log(`🔍 [TESTRUNNER] Checking: ${pendingShortAnswers.length} textarea(s) with generating attribute`);
          
          if (pendingShortAnswers.length === 0) {
            console.log('✅ [TESTRUNNER] All ShortAnswer interactions completed');
            resolve(true);
          } else if (Date.now() - startTime > maxWaitTime) {
            console.log(`⏰ [TESTRUNNER] Timeout waiting for ShortAnswer completions (${pendingShortAnswers.length} still pending)`);
            resolve(false);
          } else {
            console.log(`⏳ [TESTRUNNER] Waiting for ${pendingShortAnswers.length} ShortAnswer completions...`);
            setTimeout(checkCompletion, checkInterval);
          }
        };
        
        checkCompletion();
      });
    };
    
    await waitForAllShortAnswersCompletion();
  } else {
    console.log('👨‍🎓 [TESTRUNNER] Normal student mode - proceeding directly to results submission');
  }
  
  await sendResultsToDatabase({
    userId,
    testId: testId.value,
    publicationId,
    groupId,
    owner: publicationStore.publicationData?.owner,
    testData: testData.value
  });
  await sessionManager?.endSession(false);

  try {
    await api.patch(`/publications/${publicationId}/increment-attempts`);
    console.log("🚀 owner transmis :", publicationStore.publicationData?.owner);
  } catch (e) {
    console.error("Erreur incrément publication:", e);
  }
}

const onTimeUp = async() => {
  // ✅ PROTECTION: Refuser de sauvegarder les tests CAT en mode linéaire
  if (testData.value && testData.value.progression === 'adaptive') {
    console.error('❌ CRITICAL: CAT test time-up attempted to save as LINEAR in TestRunner!');
    console.error('🔄 Redirecting to AdaptiveTestRunner...');
    const currentQuery = new URLSearchParams(route.query).toString();
    router.replace(`/adaptive-test-runner?${currentQuery}`);
    return;
  }
  
  stopTimer.value = true;
  sendResultsToDatabase({
    userId,
    testId: testId.value,
    publicationId,
    groupId,
    owner: publicationStore.publicationData?.owner,
    testData: testData.value
  });
  await sessionManager?.endSession(false);
};



onMounted(async () => {
  await fetchTestData();
  
  // Vérifier si le test chargé est effectivement adaptatif
  if (testData.value) {
    const isTestAdaptive = testData.value.progression === 'adaptive';
    console.log('🔍 Test loaded:', {
      title: testData.value.title,
      progression: testData.value.progression,
      isAdaptive: isTestAdaptive,
      urlParamMode: adaptiveMode.value,
      levelsCount: testData.value.levels?.length || 0,
      elementsCount: testData.value.elements?.length || 0
    });
    
    // ✅ CORRECTION CRITIQUE: Rediriger les tests CAT vers AdaptiveTestRunner
    if (isTestAdaptive) {
      console.warn('🐈 CAT test detected in TestRunner - redirecting to AdaptiveTestRunner');
      const currentQuery = new URLSearchParams(route.query).toString();
      router.replace(`/adaptive-test-runner?${currentQuery}`);
      return;
    }
    
    // Mettre à jour le mode adaptatif basé sur les données réelles du test
    adaptiveMode.value = isTestAdaptive;
    
    if (isTestAdaptive && (!testData.value.levels || testData.value.levels.length === 0)) {
      console.warn('⚠️ CAT test detected but no levels structure found');
    }
  }

  if (publicationId) {
    await publicationStore.fetchPublication(publicationId);
  }

  const publicationAccess = publicationStore.publicationData?.access;

  sessionManager = useSessionManager(
    testId,
    publicationId,
    groupId,
    userId,
    publicationAccess
  );

  if (token && userId) {
    await sessionManager.startSession();
  }

  window.addEventListener('beforeunload', onBeforeUnload);
});

watch(() => publicationStore.publicationData, (val) => {
  if (val) console.log("📘 Publication data maintenant disponible :", val);
});


onUnmounted(() => {
  window.removeEventListener('beforeunload', onBeforeUnload);
});

const onBeforeUnload = async (event) => {
  if (sessionId.value && !lastPageReached.value) {
    try {
      await sessionManager?.endSession(true); // abandon
    } catch (e) {
      console.warn('Abandon non enregistré à temps');
    }
  }
};

watch(testData, () => {
  if (testData.value.pages) {
    questionIndexMap.value = getQuestionIndexMap();
  }
});
</script>

<style scoped>
.sendToServer {
  margin-left: 10px;
}
</style>
